import Fuse, { type FuseResult, type FuseResultMatch } from 'fuse.js'
import { useGetAddressBookEntries } from '@dhl/receiver-address-book-api'

import {
  type Piece,
  type PieceAddressDescriptionFn,
  type PieceSenderOrDestinationFn,
  type PieceTimeIndicationDisplayFn,
  usePieceAddressDescription,
  usePieceReceiverOrSenderName,
  usePieceTimeIndication,
} from '@dhl/pieces'
import type { ProfileAddress } from '@dhl/receiver-profile-api'

import type { AddressBookEntry } from '~/profile/model/addressBook/model.js'
import { useGetIncomingPieces } from '~/pieces/hooks/useGetIncomingPieces.js'

import { useGetOutgoingPieces } from '~/pieces/hooks/useGetOutgoingPieces.js'
import { useGetProfile } from '../../profile/model/profile/hooks.js'

type TextHighlight = [string, boolean]

export type MatchWithHighlights = SearchResultMatch & {
  highlights: [string, boolean][]
}

export type PieceForSearch = Piece & {
  addressDescription: string | null
  senderOrDestination: string
  timeIndicationDisplay: string
}

export type AddressBookEntryForSearch = Omit<AddressBookEntry, 'name'> & {
  name: string
}

export type SearchResultMatch = {
  indices: readonly [number, number][]
  key?: string
  refIndex?: number
  value?: string
}

export type SearchResult<T> = {
  item: T
  refIndex: number
  score?: number
  matches?: readonly MatchWithHighlights[]
}

export type SearchResults = {
  data: SearchResultsData
  isLoading: boolean
}

export type SearchResultsData = {
  inTransitIncomingPieces: SearchResult<PieceForSearch>[]
  deliveredIncomingPieces: SearchResult<PieceForSearch>[]
  returnedIncomingPieces: SearchResult<PieceForSearch>[]
  inTransitOutgoingPieces: SearchResult<PieceForSearch>[]
  deliveredOutgoingPieces: SearchResult<PieceForSearch>[]
  profileAddresses: SearchResult<ProfileAddress>[]
  addressBookEntries: SearchResult<AddressBookEntryForSearch>[]
  resultsFound: boolean
}

type SearchResultCategory =
  | 'inTransitIncoming'
  | 'deliveredIncoming'
  | 'returnedIncoming'
  | 'inTransitOutgoing'
  | 'deliveredOutgoing'
  | 'profileAddress'
  | 'addressBook'

const withCategory = (category: SearchResultCategory) => (item: Record<string, unknown>) => ({
  ...item,
  category,
})

const byCategory =
  (category: SearchResultCategory) =>
  ({ item }: FuseResult<Record<string, unknown>>) =>
    item.category === category

const withExtendedPieceData =
  (
    getTimeIndicationDisplay: PieceTimeIndicationDisplayFn,
    getSenderOrDestination: PieceSenderOrDestinationFn,
    getAddressDescription: PieceAddressDescriptionFn
  ) =>
  (piece: Piece) => ({
    ...piece,
    senderOrDestination: getSenderOrDestination(piece),
    addressDescription: getAddressDescription(piece) ?? '',
    timeIndicationDisplay: getTimeIndicationDisplay(piece),
  })

const withCalculatedHighlights = (match: FuseResultMatch): MatchWithHighlights => {
  const { indices, value = '' } = match
  const highlights: TextHighlight[] = []

  if (indices.length === 0) {
    highlights.push([value, false])
  } else if (indices[0][0] > 0) {
    highlights.push([value.slice(0, indices[0][0]), false])
  }

  indices.forEach(([start, end], i) => {
    const endIndex = end + 1
    highlights.push([value.slice(start, endIndex), end - start >= 1])

    if (i === indices.length - 1) {
      if (endIndex < value.length) {
        highlights.push([value.slice(endIndex, value.length), false])
      }
    } else if (endIndex < indices[i + 1][0]) {
      highlights.push([value.slice(endIndex, indices[i + 1][0]), false])
    }
  })

  return {
    ...match,
    highlights,
  }
}

const transformAddressBookEntry = (entry: AddressBookEntry) => ({
  ...entry,
  name: `${entry.name.firstName} ${entry.name.lastName}`,
})

const useGetSearchableData = (searchTerm: string) => {
  const { data: profile } = useGetProfile()
  const profileAddresses = (profile?.addresses as unknown as Record<string, unknown>[]) ?? []

  const { isLoading: isLoadingAddressBookEntries, data: addressBookEntries } = useGetAddressBookEntries({ searchTerm })

  const { data: incomingPieces, isLoading: isLoadingIncomingPieces } = useGetIncomingPieces()
  const inTransitIncomingPieces = incomingPieces?.inTransit ?? []
  const deliveredIncomingPieces = incomingPieces?.delivered ?? []
  const returnedIncomingPieces = incomingPieces?.returned ?? []

  const { data: outgoingPieces, isLoading: isLoadingOutgoingPieces } = useGetOutgoingPieces()
  const inTransitOutgoingPieces = outgoingPieces?.inTransit ?? []
  const deliveredOutgoingPieces = outgoingPieces?.delivered ?? []

  const getTimeIndication = usePieceTimeIndication()
  const getSenderOrDestination = usePieceReceiverOrSenderName()
  const getAddressDescription = usePieceAddressDescription()

  const extendPieceTransformation = withExtendedPieceData(
    getTimeIndication,
    getSenderOrDestination,
    getAddressDescription
  )

  /* eslint-disable @typescript-eslint/no-explicit-any -- explicit type not available */
  const applyTransformations =
    (...transformFns: ((input: any) => any)[]) =>
    (item: any) =>
      transformFns.reduce((acc, fn) => fn(acc), item)
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return {
    isLoading: isLoadingAddressBookEntries || isLoadingIncomingPieces || isLoadingOutgoingPieces,
    data: [
      ...inTransitIncomingPieces.map(
        applyTransformations(withCategory('inTransitIncoming'), extendPieceTransformation)
      ),
      ...deliveredIncomingPieces.map(
        applyTransformations(withCategory('deliveredIncoming'), extendPieceTransformation)
      ),
      ...returnedIncomingPieces.map(applyTransformations(withCategory('returnedIncoming'), extendPieceTransformation)),
      ...inTransitOutgoingPieces.map(
        applyTransformations(withCategory('inTransitOutgoing'), extendPieceTransformation)
      ),
      ...deliveredOutgoingPieces.map(
        applyTransformations(withCategory('deliveredOutgoing'), extendPieceTransformation)
      ),
      ...profileAddresses.map(withCategory('profileAddress')),
      ...(addressBookEntries?.map(transformAddressBookEntry).map(withCategory('addressBook')) ?? []),
    ],
  }
}

const searchKeys = [
  'barcode',
  'destination.address.postalCode',
  'destination.address.city',
  'destination.name',
  'receiver.address.postalCode',
  'receiver.address.city',
  'receiver.name',
  'addressDescription',
  'senderOrDestination',
  'name',
  'address.street',
  'address.houseNumber',
  'address.postalCode',
  'address.zipCode',
  'address.city',
  'addressDetails.firstName',
  'addressDetails.lastName',
  'addressDetails.email',
  'addressDetails.companyName',
]

export const useSearch = (searchTerm: string): SearchResults => {
  const { data: searchableData, isLoading } = useGetSearchableData(searchTerm)

  if (!searchTerm || !searchableData.length) {
    return {
      data: {
        inTransitIncomingPieces: [],
        deliveredIncomingPieces: [],
        returnedIncomingPieces: [],
        inTransitOutgoingPieces: [],
        deliveredOutgoingPieces: [],
        profileAddresses: [],
        addressBookEntries: [],
        resultsFound: false,
      },
      isLoading,
    }
  }

  const fuse = new Fuse(searchableData, {
    fieldNormWeight: 1,
    includeMatches: true,
    shouldSort: false,
    threshold: 0.3,
    keys: searchKeys,
  })

  const searchResults = fuse.search(searchTerm).map((res) => ({
    ...res,
    matches: res.matches?.map(withCalculatedHighlights),
  }))

  const results = {
    inTransitIncomingPieces: searchResults.filter(byCategory('inTransitIncoming')),
    deliveredIncomingPieces: searchResults.filter(byCategory('deliveredIncoming')),
    returnedIncomingPieces: searchResults.filter(byCategory('returnedIncoming')),
    inTransitOutgoingPieces: searchResults.filter(byCategory('inTransitOutgoing')),
    deliveredOutgoingPieces: searchResults.filter(byCategory('deliveredOutgoing')),
    profileAddresses: searchResults.filter(byCategory('profileAddress')),
    addressBookEntries: searchResults.filter(byCategory('addressBook')),
  }

  return {
    isLoading,
    data: {
      ...results,
      resultsFound: searchResults.length > 0,
    },
  }
}
